import { useStore } from '../../store/store';
import { useEffect, useRef } from 'react';
import { useHistory } from '../use-history';
import {
  setQsgSearchTerm,
  setQsgSearchPage,
  setSelectedQsgModel,
} from '../../store/slices/qsg/actions';
import {
  getQueryParams,
  getSectionFromPath,
  getSearchTermFromQuery,
  getSearchPageFromQuery,
  isGuideRoute,
  getModelYearFromHistory,
  getModelIdFromHistory,
  normalizeQueryToParams,
  paramsToSearchString,
} from '../../utils/query-param-helpers';
import {
  pushWithPathname,
  replaceWithPathname,
} from '../../utils/navigation-url';

export const useQueryParamSync = (
  hasInit: boolean,
  scrollToTop: () => void
) => {
  const [state, dispatch] = useStore();
  const history = useHistory();

  const { selectedModel, qsgSearchTerm, qsgSearchPage } = state.qsg;

  const query = getQueryParams(history);
  const isOnGuideRoute = isGuideRoute(history.location.pathname);
  const currentSection = isOnGuideRoute
    ? getSectionFromPath(history.location.pathname)
    : '';

  const searchTerm = getSearchTermFromQuery(query);
  const searchPage = getSearchPageFromQuery(query).toString();

  const modelYear = getModelYearFromHistory(history);
  const modelId = getModelIdFromHistory(history);

  // Keep refs to current store values for URL -> store sync
  const storeRef = useRef({
    qsgSearchTerm,
    qsgSearchPage,
    selectedModel,
  });

  useEffect(() => {
    storeRef.current = {
      qsgSearchTerm,
      qsgSearchPage,
      selectedModel,
    };
  }, [qsgSearchTerm, qsgSearchPage, selectedModel]);

  // Flag to prevent URL -> store sync from running immediately after a push
  const skipUrlToStoreSyncRef = useRef(false);

  // Store -> URL sync: syncs page, search, and selectedModel (only in model selector)
  useEffect(() => {
    if (!hasInit) return;

    const query = getQueryParams(history);
    const currentQuery = normalizeQueryToParams(query);
    const isOnGuideRoute = isGuideRoute(history.location.pathname);

    let needsUpdate = false;
    const paramsToUpdate: Record<string, string | undefined> = {
      ...currentQuery,
    };

    // Sync model params (only in model selector, use replace)
    if (!isOnGuideRoute) {
    if (selectedModel.modelYear !== modelYear) {
        needsUpdate = true;
          if (selectedModel.modelYear) {
          paramsToUpdate['model-year'] = selectedModel.modelYear;
        } else {
          paramsToUpdate['model-year'] = undefined;
        }
      }
      if (selectedModel.modelId !== modelId) {
        needsUpdate = true;
        if (selectedModel.modelId) {
          paramsToUpdate.model = selectedModel.modelId;
        } else {
          paramsToUpdate.model = undefined;
        }
        }
      }

    // Sync search and page (use push, batch if both change)
    const urlSearchTerm = getSearchTermFromQuery(query);
    const urlSearchPage = getSearchPageFromQuery(query);
    const searchTermChanged = qsgSearchTerm !== urlSearchTerm;
    const searchPageChanged = qsgSearchPage !== urlSearchPage;

    const needsPush = searchTermChanged || searchPageChanged;

    if (searchTermChanged) {
      if (qsgSearchTerm) {
        paramsToUpdate.search = qsgSearchTerm;
        // When search term changes, preserve page if it's set, otherwise reset to 1
        if (qsgSearchPage === 1) {
          paramsToUpdate.page = undefined;
        } else {
          paramsToUpdate.page = qsgSearchPage.toString();
        }
      } else {
        // Clearing search - remove search and page params
        paramsToUpdate.search = undefined;
        paramsToUpdate.page = undefined;
          }
      needsUpdate = true;
    } else if (searchPageChanged && qsgSearchTerm) {
      // Only update page if search term didn't change (it would have reset page to 1)
      // Only update page if there's a search term
      if (qsgSearchPage === 1) {
        paramsToUpdate.page = undefined;
      } else {
        paramsToUpdate.page = qsgSearchPage.toString();
      }
      needsUpdate = true;
    } else if (!qsgSearchTerm && urlSearchPage !== 1) {
      // Remove page param when there's no search term
      paramsToUpdate.page = undefined;
      needsUpdate = true;
    }

    if (!needsUpdate) return;

    const newSearchString = paramsToSearchString(paramsToUpdate);

    if (needsPush) {
      // Batch search and page changes into a single push
      pushWithPathname(history, history.location.pathname, newSearchString);
        skipUrlToStoreSyncRef.current = true;
        setTimeout(() => {
          skipUrlToStoreSyncRef.current = false;
        }, 0);
    } else {
      // Use replace for model param changes (only in model selector)
      replaceWithPathname(history, history.location.pathname, newSearchString);
    }
  }, [
    hasInit,
    history,
    selectedModel.modelYear,
    selectedModel.modelId,
    qsgSearchTerm,
    qsgSearchPage,
    modelYear,
    modelId,
  ]);

  // Sync URL -> store (when URL changes from browser back/forward, update store)
  useEffect(() => {
    if (!hasInit) return;

    const syncUrlToStore = () => {
      // Skip if we just did a batch push (to avoid interference)
      if (skipUrlToStoreSyncRef.current) return;

      const query = getQueryParams(history);
      const currentStore = storeRef.current;

      const urlSearchTerm = getSearchTermFromQuery(query);
      const urlSearchPage = getSearchPageFromQuery(query);

      const updates: Array<() => void> = [];
      const isSearchTermChanging = urlSearchTerm !== currentStore.qsgSearchTerm;

      if (isSearchTermChanging) {
        updates.push(() => dispatch(setQsgSearchTerm(urlSearchTerm)));
      }

      // Update search page from URL (only if there's a search term)
      if (urlSearchTerm) {
          if (urlSearchPage !== currentStore.qsgSearchPage) {
            updates.push(() => dispatch(setQsgSearchPage(urlSearchPage)));
        }
      } else {
        // If no search term, reset search page
        if (currentStore.qsgSearchPage !== 1) {
          updates.push(() => dispatch(setQsgSearchPage(1)));
        }
      }

      updates.forEach((update) => update());

      // Update model from URL or path
      const urlModelId = getModelIdFromHistory(history);
      const urlModelYear = getModelYearFromHistory(history);

      if (
        urlModelId !== currentStore.selectedModel.modelId ||
        urlModelYear !== currentStore.selectedModel.modelYear
      ) {
        dispatch(
          setSelectedQsgModel({
            modelId: urlModelId,
            modelYear: urlModelYear,
          })
        );
      }
    };

    syncUrlToStore();

    const unlisten = history.listen(() => {
      syncUrlToStore();
    });

    return unlisten;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasInit,
    history,
    // Note: We intentionally don't include store values in deps
    // This effect should only run when URL changes, not when store changes
    // We read store values inside the effect to compare with URL values
    dispatch,
    scrollToTop,
  ]);

  return {
    modelYear,
    modelId,
    currentSection,
    searchTerm,
    searchPage,
  };
};
